import { useContext } from "@nuxtjs/composition-api";
import uniqBy from "lodash-es/uniqBy";
import { htmlDecode } from "@wemade-vsf/core";
const getName = (product) => {
  if (!product) {
    return "";
  }
  return htmlDecode(product.name);
};
const getSlug = (product, category) => {
  const rewrites = product == null ? void 0 : product.url_rewrites;
  let url = (product == null ? void 0 : product.sku) ? `/p/${product.sku}` : "";
  if (!rewrites || rewrites.length === 0) {
    return url;
  }
  url = `/${rewrites[0].url}`;
  for (const rewrite of rewrites) {
    if (category && category.uid) {
      url = `/${rewrite.url}`;
      break;
    }
  }
  return url;
};
function roundPrice(price) {
  return price ? parseFloat(price.toFixed(2)) : 0;
}
const getPrice = (product, includesTax = false) => {
  let regular = 0;
  let special = null;
  let maximum = null;
  let final = null;
  let priceMatrixPrice = null;
  if (product == null ? void 0 : product.price_matrix_price) {
    priceMatrixPrice = includesTax ? product.price_matrix_price.price_incl_tax : product.price_matrix_price.price_excl_tax;
  }
  if (product == null ? void 0 : product.price_range) {
    regular = roundPrice(product.price_range.minimum_price.regular_price.value);
    final = roundPrice(priceMatrixPrice ? priceMatrixPrice : product.price_range.minimum_price.final_price.value);
    maximum = roundPrice(product.price_range.maximum_price.final_price.value);
    if (final < regular) {
      special = final;
    }
  }
  return {
    regular,
    special,
    maximum,
    final
  };
};
const getGallery = (product, maxGallerySize = 20) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
  const { app } = useContext();
  const mergeVariants = ((_b = (_a = app.$wm) == null ? void 0 : _a.catalog) == null ? void 0 : _b.mergeVariationImages) || false;
  const images = [];
  if (!((_c = product == null ? void 0 : product.media_gallery) == null ? void 0 : _c.length) && !((_e = (_d = product == null ? void 0 : product.configurable_product_options_selection) == null ? void 0 : _d.media_gallery) == null ? void 0 : _e.length)) {
    return images;
  }
  let gallery = [];
  if (mergeVariants) {
    gallery = [
      ...((_g = (_f = product.configurable_product_options_selection) == null ? void 0 : _f.media_gallery) == null ? void 0 : _g.length) ? (_h = product.configurable_product_options_selection) == null ? void 0 : _h.media_gallery : [],
      ...product.media_gallery
    ];
  } else {
    gallery = ((_i = product.configurable_product_options_selection) == null ? void 0 : _i.variant) && ((_j = product.configurable_product_options_selection) == null ? void 0 : _j.media_gallery.length) ? product.configurable_product_options_selection.media_gallery : product.media_gallery;
  }
  gallery = uniqBy(gallery, (item) => item.url);
  for (const galleryItem of gallery) {
    images.push({
      small: galleryItem.url,
      normal: galleryItem.url,
      big: galleryItem.url,
      ...((_k = galleryItem.video_content) == null ? void 0 : _k.video_url) ? { video: galleryItem.video_content } : {}
    });
  }
  return images.slice(0, maxGallerySize);
};
const getProductSku = (product) => {
  var _a, _b;
  if (!product)
    return "";
  if ((_b = (_a = product == null ? void 0 : product.configurable_product_options_selection) == null ? void 0 : _a.variant) == null ? void 0 : _b.sku) {
    return product.configurable_product_options_selection.variant.sku;
  }
  return product["sku"] ? product.sku : "";
};
const getCoverImage = (product) => {
  if (!product || !product.image) {
    return null;
  }
  return product.image.url;
};
const getProductThumbnailImage = (product) => {
  if (!product || !product.small_image && !product.thumbnail) {
    return null;
  }
  return product.small_image ? product.small_image.url : product.thumbnail.url;
};
const getAttributes = (products, _filterByAttributeName) => {
  if (!products || !(products == null ? void 0 : products.configurable_options)) {
    return {};
  }
  const attributes = {};
  const configurableOptions = products.configurable_options;
  for (const option of configurableOptions) {
    attributes[option.attribute_code] = {
      code: option.attribute_code,
      label: option.label,
      value: option.values.map((value) => {
        const obj = {};
        obj[value.value_index] = value.label;
        return obj;
      })
    };
  }
  return attributes;
};
const getDescription = (product) => {
  if (!product || !(product == null ? void 0 : product.description)) {
    return "";
  }
  return product.description.html;
};
const getShortDescription = (product) => {
  if (!product || !product.short_description) {
    return "";
  }
  return product.short_description.html;
};
const getCategoryIds = (product) => {
  const categoryIds = [];
  if (!(product == null ? void 0 : product.categories)) {
    return categoryIds;
  }
  return product.categories.map((category) => category.uid);
};
const getCategory = (product, currentUrlPath) => {
  if (!(product == null ? void 0 : product.categories) || (product == null ? void 0 : product.categories.length) === 0) {
    return null;
  }
  const categories = currentUrlPath.split("/");
  categories.pop();
  if (categories.length === 0) {
    return null;
  }
  const categoryPath = categories.join("/");
  for (const category of product.categories) {
    if (`/${category.url_path}` === categoryPath) {
      return category;
    }
  }
  return null;
};
const getId = (product) => product.uid;
const getTypeId = (product) => product.__typename;
const getCategoryBreadcrumbs = (category) => {
  let breadcrumbs = [];
  if (!category) {
    return [];
  }
  if (Array.isArray(category == null ? void 0 : category.breadcrumbs)) {
    breadcrumbs = category.breadcrumbs.map((breadcrumb) => ({
      text: breadcrumb.category_name,
      link: `/${breadcrumb.category_url_path}${category.url_suffix || ""}`
    }));
  }
  breadcrumbs.push({
    text: category.name,
    link: `/${category.url_path}${category.url_suffix || ""}`
  });
  return breadcrumbs;
};
const getBreadcrumbs = (product, category) => {
  var _a, _b, _c;
  let breadcrumbs = [];
  if (!product) {
    return breadcrumbs;
  }
  if (category) {
    breadcrumbs = getCategoryBreadcrumbs(category);
  }
  breadcrumbs.push({
    text: getName(product),
    link: `/${(_c = (_b = (_a = product == null ? void 0 : product.url_rewrites) == null ? void 0 : _a[0]) == null ? void 0 : _b.url) != null ? _c : product.url_key}`
  });
  return breadcrumbs;
};
const getProductRelatedProduct = (product) => (product == null ? void 0 : product.related_products) || [];
const getProductUpsellProduct = (product) => (product == null ? void 0 : product.upsell_products) || [];
const getSwatchData = (swatchData) => swatchData == null ? void 0 : swatchData.value;
const sortProduct = (a, b) => a.position - b.position;
const getGroupedProducts = (product) => {
  var _a;
  return product.__typename === "GroupedProduct" && ((_a = product == null ? void 0 : product.items) == null ? void 0 : _a.sort(sortProduct));
};
const getBundleProducts = (product) => {
  var _a;
  return product.__typename === "BundleProduct" && ((_a = product == null ? void 0 : product.items) == null ? void 0 : _a.sort(sortProduct));
};
const productGetters = {
  getAttributes,
  getBreadcrumbs,
  getCategory,
  getCategoryIds,
  getCoverImage,
  getDescription,
  getGallery,
  getId,
  getName,
  getPrice,
  getProductRelatedProduct,
  getProductSku,
  getProductThumbnailImage,
  getProductUpsellProduct,
  getShortDescription,
  getSlug,
  getTypeId,
  getSwatchData,
  getGroupedProducts,
  getBundleProducts
};
var productGetters_default = productGetters;
export {
  productGetters_default as default,
  getAttributes,
  getBreadcrumbs,
  getBundleProducts,
  getCategory,
  getCategoryIds,
  getCoverImage,
  getDescription,
  getGallery,
  getGroupedProducts,
  getId,
  getName,
  getPrice,
  getProductRelatedProduct,
  getProductSku,
  getProductThumbnailImage,
  getProductUpsellProduct,
  getShortDescription,
  getSlug,
  getSwatchData,
  getTypeId
};
